import React, { useState, useMemo } from "react";
import PageLoader from "../components/ui/PageLoader";
import "../assets/css/style.css";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import Modal from "../components/ui/Modal";
import {
  useContractorCompaniesQuery,
  useAddContractorCompanyMutation,
  useUpdateContractorCompanyMutation,
  useDeleteContractorCompanyMutation,
} from "apis/contractorCompany";
import { FaPlus } from "react-icons/fa6";
import ContractorCompanyEdit from "components/contractorCompany/Edit";
import ContractorCompanyView from "components/contractorCompany/View";
import Table from "components/ui/Table";

const STATUS_COLORS = {
  active: "bg-green-100 text-green-800",
  inactive: "bg-red-100 text-red-800",
};

const formatABN = (abn) => {
  if (!abn) return null;
  return abn.replace(/(\d{2})(\d{3})(\d{3})(\d{3})/, "$1 $2 $3 $4");
};

const ContractorCompanies = () => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const pageSize = 10;

  const {
    data,
    isPending: isDataPending,
    isError: isDataError,
    error: dataError,
  } = useContractorCompaniesQuery();

  const { mutate: createCompany, isLoading: isCreateLoading } =
    useAddContractorCompanyMutation();
  const { mutate: updateCompany } = useUpdateContractorCompanyMutation();
  const { mutate: deleteCompany, isLoading: isDeleteLoading } =
    useDeleteContractorCompanyMutation();

  const columns = useMemo(
    () => [
      {
        accessorKey: "index",
        header: "#",
        cell: (_, rowIndex, currentPage) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
        isFilter: false,
        isSortable: false,
      },
      {
        accessorKey: "name",
        header: "Company Name",
        cell: (row) => (
          <div className="min-w-0 flex items-center gap-3">
            {row.logo && (
              <img
                src={row.logo}
                alt={row.name}
                className="w-8 h-8 rounded-full object-contain flex-shrink-0"
              />
            )}
            <span className="font-medium truncate">{row.name}</span>
          </div>
        ),
        isFilter: true,
        minWidth: "200px",
      },
      {
        accessorKey: "email",
        header: "Email",
        cell: (row) => <span className="text-sm">{row.email || "-"}</span>,
        isFilter: true,
      },
      {
        accessorKey: "mobile",
        header: "Mobile",
        cell: (row) => <span className="text-sm">{row.mobile || "-"}</span>,
        isFilter: false,
      },
      {
        accessorKey: "abn",
        header: "ABN",
        cell: (row) => (
          <span className="text-sm font-mono">{formatABN(row.abn) || "-"}</span>
        ),
        isFilter: true,
      },
      {
        accessorKey: "licenseNumber",
        header: "License #",
        cell: (row) => (
          <span className="text-sm">{row.licenseNumber || "-"}</span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: (row) => {
          const status = row.status || "active";
          return (
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${
                STATUS_COLORS[status] || "bg-gray-100 text-gray-800"
              }`}
            >
              {status}
            </span>
          );
        },
        isFilter: false,
      },
      {
        accessorKey: "createdAt",
        header: "Created",
        cell: (row) => (
          <span className="text-sm">
            {new Date(row.createdAt).toLocaleDateString("en-AU", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}
          </span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "actions",
        header: "Actions",
        cell: (row) => (
          <div className="flex items-center space-x-4">
            <button
              onClick={() => {
                setSelectedId(row._id);
                setIsEditOpen(true);
              }}
              className="edit-icon"
              title="Edit Company"
            >
              <FiEdit2 fontSize={20} />
            </button>
            <button
              onClick={() => {
                setSelectedId(row._id);
                setIsDeleteOpen(true);
              }}
              className="delete-icon"
              title="Delete Company"
            >
              <FiTrash2 fontSize={20} />
            </button>
          </div>
        ),
        isFilter: false,
        isSortable: false,
        stopPropagation: true,
        minWidth: "100px",
      },
    ],
    []
  );

  if (isDataPending) return <PageLoader />;

  if (isDataError) {
    return (
      <div className="container mx-auto">
        <div className="text-center py-8">
          <p className="text-red-600">
            Error loading contractor companies: {dataError?.message}
          </p>
        </div>
      </div>
    );
  }

  const selectedCompany = data?.find((c) => c._id === selectedId);

  const handleEditSubmit = (formData) => {
    updateCompany({ id: selectedId, ...formData });
    setIsEditOpen(false);
  };

  const handleCreateSubmit = (formData) => {
    createCompany(formData);
    setIsAddOpen(false);
  };

  const handleDelete = () => {
    deleteCompany(selectedId);
    setIsDeleteOpen(false);
  };

  const handleRowClick = (row) => {
    setSelectedId(row._id);
    setIsViewOpen(true);
  };

  const AddButton = () => (
    <button
      className="black-button flex items-center"
      onClick={() => setIsAddOpen(true)}
      disabled={isCreateLoading}
    >
      <FaPlus size={14} className="mr-2" />
      Add Company
    </button>
  );

  return (
    <>
      {/* Edit Modal */}
      <Modal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        title="Edit Contractor Company"
        className="max-w-3xl"
      >
        <ContractorCompanyEdit
          initialData={selectedCompany}
          onSubmit={handleEditSubmit}
        />
      </Modal>

      {/* Add Modal */}
      <Modal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        title="Add Contractor Company"
        className="max-w-3xl"
      >
        <ContractorCompanyEdit initialData={{}} onSubmit={handleCreateSubmit} />
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        title="Delete Contractor Company"
      >
        <div>
          <p className="mb-4">
            Are you sure you want to delete "{selectedCompany?.name}"? This will
            set the company status to inactive.
          </p>
          <div className="w-full flex gap-2 justify-end py-4">
            <button
              onClick={() => setIsDeleteOpen(false)}
              className="gray-button"
              disabled={isDeleteLoading}
            >
              No
            </button>
            <button
              onClick={handleDelete}
              className="black-button"
              disabled={isDeleteLoading}
            >
              Yes
            </button>
          </div>
        </div>
      </Modal>

      {/* View Modal */}
      <Modal
        isOpen={isViewOpen}
        onClose={() => setIsViewOpen(false)}
        title="Contractor Company Details"
        className="max-w-3xl"
      >
        <ContractorCompanyView company={selectedCompany} />
      </Modal>

      {/* Table */}
      <div className="container mx-auto">
        <Table
          title="Contractor Companies"
          columns={columns}
          data={data || []}
          pageSize={pageSize}
          ButtonCmp={AddButton}
          onRowClick={handleRowClick}
        />
      </div>
    </>
  );
};

export default ContractorCompanies;